import type {
  CancellationOfferPage,
  CancellationOfferResponse,
  CancellationRetentionPage,
  FrequencyOptionsResponse,
  PageViewEntry,
  PageViewReport,
} from '@api/customer-portal-api';

/*
 * The cancel-flow page walk, as pure functions (no React, no I/O). index.tsx keeps the
 * state and renders; these rules decide which pages are shown, in what order, where a
 * forward button leads, the "Step N of M" count, and which skip reports are sent.
 *
 * Backend contract (lasso-backend docs/wiki/architecture/cancellation-offers.md):
 * - The "offer" token shows offer_pages[0]; older backends send only offer_page (pause).
 * - A change-frequency page is shown only after its eligibility check says eligible. It
 *   is skipped on availability "unavailable", an ineligible answer, or an error
 *   (check_failed), and a skip is reported only when the walk passes over the page.
 */

export type RetentionOffer = NonNullable<CancellationOfferResponse['offer']>;

// Eligibility of the change-frequency offer page during one cancel flow.
export type FrequencyCheck =
  | { status: 'checking' }
  | { status: 'eligible'; data: FrequencyOptionsResponse }
  | { status: 'skipped'; reason: string };

export interface WalkState {
  // null unless the offer page is a change-frequency page.
  check: FrequencyCheck | null;
}

export type WalkStep =
  | { kind: 'page'; page: CancellationRetentionPage }
  | { kind: 'wait' } // the next page is a change-frequency page still being checked
  | { kind: 'end' }; // no page left: the next step is the cancellation

// What a page's forward button does: go on, wait on "Checking options…", or cancel.
export type ForwardCta = 'continue' | 'checking' | 'cancel';

export const DEFAULT_PAGE_ORDER: readonly CancellationRetentionPage[] = [
  'before_you_go',
  'offer',
  'cancellation_reason',
];

// Skip code for an eligibility check that errored (non-2xx or no answer), and the
// fallback when the backend gives no code. One of the backend's SKIP_REASON_CODES.
export const CHECK_FAILED = 'check_failed';

const isPageToken = (token: string): token is CancellationRetentionPage =>
  (DEFAULT_PAGE_ORDER as readonly string[]).includes(token);

// Same rule as the backend's normalize_page_order: known tokens only, each once, then any
// missing ones in the default order.
export const normalizePageOrder = (
  raw: readonly string[] | null | undefined,
): CancellationRetentionPage[] => {
  const order: CancellationRetentionPage[] = [];
  for (const token of raw ?? []) {
    if (isPageToken(token) && !order.includes(token)) order.push(token);
  }
  for (const token of DEFAULT_PAGE_ORDER) {
    if (!order.includes(token)) order.push(token);
  }
  return order;
};

// The page the "offer" token shows. Older backends send only the pause offer_page.
export const offerPageOf = (
  offer: RetentionOffer | null | undefined,
): CancellationOfferPage | null => {
  if (!offer) return null;
  if (Array.isArray(offer.offer_pages)) return offer.offer_pages[0] ?? null;
  return offer.offer_page ?? null;
};

export const isFrequencyPage = (page: CancellationOfferPage | null | undefined): boolean =>
  page?.offer_type === 'change_frequency';

const pagePresent = (offer: RetentionOffer, page: CancellationRetentionPage): boolean => {
  if (page === 'before_you_go') return !!offer.before_you_go;
  if (page === 'cancellation_reason') return !!offer.cancellation_reason;
  return !!offerPageOf(offer);
};

// The offer's pages in flow order, before any skip.
const flowOrder = (offer: RetentionOffer): CancellationRetentionPage[] =>
  normalizePageOrder(offer.page_order).filter((page) => pagePresent(offer, page));

// The check state when the flow opens: none for a pause page, a skip (no options call)
// for an unavailable page, otherwise one options call.
export const initialFrequencyCheck = (
  page: CancellationOfferPage | null | undefined,
): FrequencyCheck | null => {
  if (!page || !isFrequencyPage(page)) return null;
  if (!page.offer_page_id) return { status: 'skipped', reason: CHECK_FAILED };
  if (page.availability?.status === 'unavailable') {
    return { status: 'skipped', reason: page.availability.reason || CHECK_FAILED };
  }
  return { status: 'checking' };
};

// The check state from a 200 options answer. A skip always carries a code.
export const frequencyCheckFromResponse = (data: FrequencyOptionsResponse): FrequencyCheck => {
  if (data.eligible && data.options.length > 0) return { status: 'eligible', data };
  return {
    status: 'skipped',
    reason: data.ineligible_reason || (data.eligible ? 'no_options' : CHECK_FAILED),
  };
};

// Whether the offer page may still be shown, given current knowledge (a running check
// counts as shown).
const offerShowable = (offer: RetentionOffer, state: WalkState): boolean => {
  const page = offerPageOf(offer);
  if (!page) return false;
  if (!isFrequencyPage(page)) return true;
  return state.check !== null && state.check.status !== 'skipped';
};

// The pages that will still be shown, in order (drives "Step N of M").
export const buildRetentionPages = (
  offer: RetentionOffer,
  state: WalkState,
): CancellationRetentionPage[] =>
  flowOrder(offer).filter((page) => page !== 'offer' || offerShowable(offer, state));

// Where moving forward from `from` (null = the flow is opening) leads.
export const nextStep = (
  offer: RetentionOffer,
  state: WalkState,
  from: CancellationRetentionPage | null,
): WalkStep => {
  const order = flowOrder(offer);
  const start = from === null ? 0 : order.indexOf(from) + 1;
  for (const page of order.slice(start)) {
    if (page !== 'offer') return { kind: 'page', page };
    if (!offerShowable(offer, state)) continue;
    return state.check?.status === 'checking' ? { kind: 'wait' } : { kind: 'page', page };
  }
  return { kind: 'end' };
};

export const forwardCta = (
  offer: RetentionOffer,
  state: WalkState,
  from: CancellationRetentionPage | null,
): ForwardCta => {
  const step = nextStep(offer, state, from);
  if (step.kind === 'page') return 'continue';
  return step.kind === 'wait' ? 'checking' : 'cancel';
};

// Whether `from` is the last shown page only because a change-frequency page after it was
// skipped: its forward button then cancels, so it reads red "Continue to cancel". False
// when it is last for any other reason (a pause-only or Before-you-go-only offer, or an
// offer page ordered before it), which keeps today's neutral "Continue".
export const lastBecauseSkipped = (
  offer: RetentionOffer,
  state: WalkState,
  from: CancellationRetentionPage | null,
): boolean => {
  if (nextStep(offer, state, from).kind !== 'end') return false;
  if (!isFrequencyPage(offerPageOf(offer)) || state.check?.status !== 'skipped') return false;
  const order = flowOrder(offer);
  return order.indexOf('offer') > (from === null ? -1 : order.indexOf(from));
};

// The page Back returns to, or null on the first shown page.
export const previousPage = (
  offer: RetentionOffer,
  state: WalkState,
  from: CancellationRetentionPage,
): CancellationRetentionPage | null => {
  const pages = buildRetentionPages(offer, state);
  const index = pages.indexOf(from);
  return index > 0 ? pages[index - 1] : null;
};

export const stepOf = (
  offer: RetentionOffer,
  state: WalkState,
  current: CancellationRetentionPage | null,
): { current: number; total: number } | null => {
  const pages = buildRetentionPages(offer, state);
  const index = current ? pages.indexOf(current) : -1;
  return index < 0 ? null : { current: index + 1, total: pages.length };
};

// The skip report for moving from `from` to `to` (null = flow start / the cancellation),
// or null. Only a skipped change-frequency page the move passes over is reported.
export const skipPassed = (
  offer: RetentionOffer,
  state: WalkState,
  from: CancellationRetentionPage | null,
  to: CancellationRetentionPage | null,
): PageViewReport | null => {
  const page = offerPageOf(offer);
  if (!page?.offer_page_id || !isFrequencyPage(page)) return null;
  const check = state.check;
  if (check?.status !== 'skipped') return null;
  const order = flowOrder(offer);
  const at = order.indexOf('offer');
  const fromIndex = from === null ? -1 : order.indexOf(from);
  const toIndex = to === null ? order.length : order.indexOf(to);
  if (!(fromIndex < at && at < toIndex)) return null;
  return { page: 'offer', offer_page_id: page.offer_page_id, skipped_reason: check.reason };
};

// The view report for a page the customer landed on.
export const viewEntry = (
  offer: RetentionOffer,
  page: CancellationRetentionPage,
): PageViewEntry => {
  if (page !== 'offer') return page;
  const id = offerPageOf(offer)?.offer_page_id;
  return id ? { page: 'offer', offer_page_id: id } : 'offer';
};

// The `page` (and the offer page's id) of a resolve made on `page`.
export const outcomePage = (
  offer: RetentionOffer,
  page: CancellationRetentionPage | null | undefined,
): { page?: CancellationRetentionPage; offer_page_id?: string } => {
  if (!page) return {};
  const id = page === 'offer' ? offerPageOf(offer)?.offer_page_id : null;
  return id ? { page, offer_page_id: id } : { page };
};

const reportKey = (entry: PageViewEntry): string =>
  typeof entry === 'string'
    ? `${entry}||`
    : `${entry.page}|${entry.offer_page_id ?? ''}|${entry.skipped_reason ?? ''}`;

// Append reports, keeping one of each (the backend also dedupes; this keeps the request
// under its 10-entry cap).
export const mergeReports = (existing: PageViewEntry[], add: PageViewEntry[]): PageViewEntry[] => {
  const seen = new Set(existing.map(reportKey));
  const merged = [...existing];
  for (const entry of add) {
    const key = reportKey(entry);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(entry);
    }
  }
  return merged;
};
